import * as z from 'zod';
import { AtoN, AtoNVirtualEnum, Pss } from '@ais-ui/api-spec/config';
import {
  dateRangeSchema,
  numericFieldSchema,
  timeFormatHoursMinutesSecondsSchema,
} from '@ais-ui/utils';
import { grabAtonInitialGroups } from '../../Groups/grabInitialGroups';
import { Group } from '@ais-ui/websockets';
import { SelectType } from '@ais-ui/ui-core';
import { timeStringToSeconds } from '../../utils/timeStringToSeconds';
import { secondsToTimeString } from '../../utils/secondsToTimeString';

export const mutatePhysicalAtonFormSchema = z.object({
  name: z.string(),
  mmsi: numericFieldSchema,
  latitude: numericFieldSchema,
  longitude: numericFieldSchema,
  description: z.string(),
  warningTimeout: timeFormatHoursMinutesSecondsSchema,
  failureTimeout: timeFormatHoursMinutesSecondsSchema,
  offPositionAlarm: z.string().optional(),
  alarms: z.array(
    z.object({
      name: z.string(),
      email: z.string().email().or(z.literal('')).optional(),
      phone: numericFieldSchema.optional(),
      url: z.string().url().or(z.literal('')).optional(),
    })
  ),
  selectedGroups: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
    })
  ),
});
export type MutatePhysicalAtonFormValues = z.infer<
  typeof mutatePhysicalAtonFormSchema
>;

const convertOffPositionToBackend = (
  offPositionAlarm: string | undefined
): number => {
  switch (offPositionAlarm) {
    case 'none':
      return 0;
    case 'warning':
      return 1;
    case 'alert':
      return 2;
    default:
      return 0;
  }
};

const convertOffPositionToFrontend = (
  offPositionAlarm: number | undefined
): string => {
  switch (offPositionAlarm) {
    case 0:
      return 'none';
    case 1:
      return 'warning';
    case 2:
      return 'alert';
    default:
      console.warn('Unknown offPosition value', offPositionAlarm);
      return 'none';
  }
};

export const convertPhysicalAtonValuesToBackend = (
  data: MutatePhysicalAtonFormValues
): AtoN => {
  return {
    name: data.name,
    mmsi: data.mmsi,
    position: {
      type: 'Point',
      coordinates: [data.longitude, data.latitude],
    },
    description: data.description,
    broadcastInterval: 360, // This isn't relevant for physical aton, but was set to 360 in AISWeb
    warningTimeout: timeStringToSeconds(data.warningTimeout),
    failureTimeout: timeStringToSeconds(data.failureTimeout),
    virtual: 0,
    offPosAlarm: convertOffPositionToBackend(data.offPositionAlarm),
    alarmSetup: data.alarms.map((alarm) => ({
      name: alarm.name,
      sms: alarm.phone ? String(alarm.phone) : '',
      email: alarm.email ? alarm.email : '',
      url: alarm.url ? alarm.url : '',
    })),
    groups: data.selectedGroups.map((group) => group.value),
    owner: '',
    sizeA: 0,
    sizeB: 0,
    sizeC: 0,
    sizeD: 0,
  };
};

export const convertBackendValuesToPhysicalAton = (
  aton: AtoN,
  groups: Group[] | null | undefined
) => {
  let latitude = undefined;
  let longitude = undefined;
  if (aton?.position?.coordinates) {
    latitude = aton.position.coordinates[1];
    longitude = aton.position.coordinates[0];
  }

  return {
    name: aton.name,
    mmsi: aton.mmsi,
    latitude: latitude,
    longitude: longitude,
    description: aton.description,
    warningTimeout: secondsToTimeString(aton.warningTimeout as number),
    failureTimeout: secondsToTimeString(aton.failureTimeout as number),
    offPositionAlarm: convertOffPositionToFrontend(aton.offPosAlarm),
    alarms: aton.alarmSetup
      ? aton.alarmSetup.map((alarm) => ({
          name: alarm.name,
          email: alarm.email,
          phone: alarm.sms ? parseInt(alarm.sms, 10) : undefined,
          url: alarm.url,
        }))
      : [],
    selectedGroups: grabAtonInitialGroups(aton, groups),
  };
};

export const mutateVirtualAtonFormSchema = z.object({
  enabled: z.boolean(),
  name: z.string(),
  mmsi: numericFieldSchema,
  latitude: numericFieldSchema,
  longitude: numericFieldSchema,
  description: z.string(),
  type: z.string(),
  virtual: z.string(),
  sizeA: numericFieldSchema,
  sizeB: numericFieldSchema,
  sizeC: numericFieldSchema,
  sizeD: numericFieldSchema,
  broadcastInterval: timeFormatHoursMinutesSecondsSchema,
  warningTimeout: timeFormatHoursMinutesSecondsSchema,
  failureTimeout: timeFormatHoursMinutesSecondsSchema,
  schedule: z.array(dateRangeSchema),
  pss: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    )
    .min(1, 'There must be at least one base station sending the message.'),
  chained: z.string().optional(),
  selectedGroups: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
    })
  ),
});

export type MutateVirtualAtonFormValues = z.infer<
  typeof mutateVirtualAtonFormSchema
>;

export const virtualAtonMessageTypes = [
  {
    label: 'Not specified',
    value: '0',
  },
  {
    label: 'Reference point',
    value: '1',
  },
  {
    label: 'RACON',
    value: '2',
  },
  {
    label: 'Structure off shore',
    value: '3',
  },
  {
    label: 'Emergency wreck mark',
    value: '4',
  },
  {
    label: 'Light, without sectors',
    value: '5',
  },
  {
    label: 'Light, with sectors',
    value: '6',
  },
  {
    label: 'Light light front',
    value: '7',
  },
  {
    label: 'Light light rear',
    value: '8',
  },
  {
    label: 'Beacon, cardinal N',
    value: '9',
  },
  {
    label: 'Beacon, cardinal E',
    value: '10',
  },
  {
    label: 'Beacon, cardinal S',
    value: '11',
  },
  {
    label: 'Beacon, cardinal W',
    value: '12',
  },
  {
    label: 'Beacon, port hand',
    value: '13',
  },
  {
    label: 'Beacon, starboard hand',
    value: '14',
  },
  {
    label: 'Beacon, preferred channel port hand',
    value: '15',
  },
  {
    label: 'Beacon, preferred channel starboard hand',
    value: '16',
  },
  {
    label: 'Beacon, isolated danger',
    value: '17',
  },
  {
    label: 'Beacon, safe water',
    value: '18',
  },
  {
    label: 'Beacon, special mark',
    value: '19',
  },
  {
    label: 'Cardinal N',
    value: '20',
  },
  {
    label: 'Cardinal E',
    value: '21',
  },
  {
    label: 'Cardinal S',
    value: '22',
  },
  {
    label: 'Cardinal W',
    value: '23',
  },
  {
    label: 'Port hand',
    value: '24',
  },
  {
    label: 'Starboard hand',
    value: '25',
  },
  {
    label: 'Preferred channel port hand',
    value: '26',
  },
  {
    label: 'Preferred channel starboard hand',
    value: '27',
  },
  {
    label: 'Isolated danger',
    value: '28',
  },
  {
    label: 'Safe water',
    value: '29',
  },
  {
    label: 'Special mark',
    value: '30',
  },
  {
    label: 'Light vessel/LANBY/RIGS',
    value: '31',
  },
  {
    label: 'DGNSS station',
    value: '32',
  },
  {
    label: 'Fog signal',
    value: '33',
  },
];

export const convertVirtualAtonValuesToBackend = (
  data: MutateVirtualAtonFormValues
): AtoN => {
  return {
    enabled: true,
    uuid: '',
    name: data.name,
    mmsi: data.mmsi,
    position: {
      type: 'Point',
      coordinates: [data.longitude, data.latitude],
    },
    description: data.description,
    broadcastInterval: timeStringToSeconds(data.broadcastInterval),
    warningTimeout: timeStringToSeconds(data.warningTimeout),
    failureTimeout: timeStringToSeconds(data.failureTimeout),
    type: parseInt(data.type, 10),
    virtual: parseInt(data.virtual, 10) as AtoNVirtualEnum,
    groups: data.selectedGroups.map((group) => group.value),
    schedule: data.schedule, // start and end has format '2024-03-19T07:40:00Z'
    pss: data.pss.map((pssItem) => pssItem.value),
    owner: '',
    chainedaton: data.chained === 'no-chain' ? '' : data.chained,
    sizeA: data.sizeA,
    sizeB: data.sizeB,
    sizeC: data.sizeC,
    sizeD: data.sizeD,
  };
};

export const grabAtonInitialPss = (
  aton: AtoN | undefined,
  pss: Pss[] | null | undefined
): SelectType[] => {
  const atonPss = aton?.pss || [];

  const matchingPSS =
    pss?.filter(
      (pssItem) =>
        atonPss.includes(pssItem.uuid as string) ||
        atonPss.includes(pssItem.name as string)
    ) || [];

  let initialPssSelectables: SelectType[] = [];
  if (matchingPSS) {
    initialPssSelectables = matchingPSS.map((pssItem) => ({
      label: pssItem.name as string,
      value: pssItem.uuid as string,
    }));
  }

  return initialPssSelectables;
};

export const convertBackendValuesToVirtualAton = (
  aton: AtoN,
  groups: Group[] | null | undefined,
  pss: Pss[] | null | undefined
) => {
  let latitude = undefined;
  let longitude = undefined;
  if (aton?.position?.coordinates) {
    latitude = aton.position.coordinates[1];
    longitude = aton.position.coordinates[0];
  }

  return {
    enabled: aton.enabled,
    name: aton.name,
    mmsi: aton.mmsi,
    latitude: latitude,
    longitude: longitude,
    description: aton.description,
    broadcastInterval: secondsToTimeString(aton.broadcastInterval as number),
    warningTimeout: secondsToTimeString(aton.warningTimeout as number),
    failureTimeout: secondsToTimeString(aton.failureTimeout as number),
    type: String(aton.type),
    virtual: String(aton.virtual),
    schedule: aton.schedule,
    pss: grabAtonInitialPss(aton, pss),
    chained: aton.chainedaton,
    sizeA: aton.sizeA,
    sizeB: aton.sizeB,
    sizeC: aton.sizeC,
    sizeD: aton.sizeD,
    selectedGroups: grabAtonInitialGroups(aton, groups),
  };
};
